import { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { api, type Preferences, type PublicUser } from "./lib/api";
import { LoginPage } from "./modules/shell/LoginPage";
import { AppShell } from "./modules/shell/AppShell";
import { MailWorkspace } from "./modules/mail/MailWorkspace";
import { CalendarWorkspace } from "./modules/calendar/CalendarWorkspace";
import { ContactsWorkspace } from "./modules/contacts/ContactsWorkspace";
import { SettingsWorkspace } from "./modules/settings/SettingsWorkspace";
import { ToastProvider } from "./modules/shell/Toast";
import { ConfirmProvider } from "./modules/shell/ConfirmDialog";

export function App() {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [booting, setBooting] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const me = await api.me();
        setUser(me.user);
        await api.csrf().catch(() => undefined);
        const p = await api.preferences();
        setPrefs(p.preferences);
        document.documentElement.setAttribute("data-theme", p.preferences.theme || "dawn");
      } catch {
        setUser(null);
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  const authed = useMemo(() => Boolean(user), [user]);

  if (booting) {
    return (
      <div className="auth-shell">
        <div className="brand">
          <span className="brand-mark">M</span> Mailstorm
        </div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <ConfirmProvider>
      <Routes>
        <Route
          path="/login"
          element={
            authed ? (
              <Navigate to="/mail" replace />
            ) : (
              <LoginPage
                onSuccess={async (u) => {
                  setUser(u);
                  try {
                    const p = await api.preferences();
                    setPrefs(p.preferences);
                    document.documentElement.setAttribute("data-theme", p.preferences.theme || "dawn");
                  } catch {
                    /* prefs optional if session flaky — mail still loads via Bearer */
                  }
                  navigate("/mail");
                }}
              />
            )
          }
        />
        <Route
          path="/"
          element={authed ? <Navigate to="/mail" replace /> : <Navigate to="/login" replace />}
        />
        <Route
          element={
            authed ? (
              <AppShell
                user={user!}
                prefs={prefs}
                onPrefs={setPrefs}
                onLogout={async () => {
                  await api.logout().catch(() => undefined);
                  setUser(null);
                  navigate("/login");
                }}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route path="/mail/*" element={<MailWorkspace prefs={prefs} />} />
          <Route path="/calendar/*" element={<CalendarWorkspace />} />
          <Route path="/contacts/*" element={<ContactsWorkspace />} />
          <Route
            path="/settings/*"
            element={
              <SettingsWorkspace
                user={user!}
                prefs={prefs}
                onPrefs={(p) => {
                  setPrefs(p);
                  document.documentElement.setAttribute("data-theme", p.theme || "dawn");
                }}
              />
            }
          />
        </Route>
        <Route path="*" element={<Navigate to={authed ? "/mail" : "/login"} replace />} />
      </Routes>
      </ConfirmProvider>
    </ToastProvider>
  );
}
